import React, { useEffect, useRef, useState } from "react";
import NavBar from "../../components/NavBar/NavBar";
import SideBar from "../../components/SideBar/SideBar";
import { ToastContainer, toast } from "react-toastify";
import "./UploadScreen.css";
import documentSvg from "../../assets/document.svg";
import upload from "../../assets/upload-icon.svg";
import {
  getuploadMedia,
  uploadMedia, // This is your POST /api/upload handler
} from "../../services/uploadService";
import UploadTable from "../../components/Table/UploadTable";
import { useLocation } from "react-router-dom";

const UploadScreen = () => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const initialPageNumber = queryParams.get("pageValue") || 1;
  const initialRowPerPage = queryParams.get("rowsPerPage") || 10;
  const initialSortBy = queryParams.get("sortBy") || "createdAt";
  const initialSortorder = queryParams.get("sortOrder") || "desc";

  const [rowsPerPage, setRowsPerPage] = useState(initialRowPerPage);
  const [pageNumber, setPageNumber] = useState(initialPageNumber);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortorder);

  const [uploading, setUploading] = useState(false);

  const allowedTypes = [
    "audio/mp3",
    "audio/mpeg",
    "audio/x-m4a",
    "audio/wav",
    "video/mp4",
  ];

  const getMediaDuration = (file) => {
    return new Promise((resolve, reject) => {
      const media = document.createElement("video");
      media.preload = "metadata";

      media.onloadedmetadata = () => {
        URL.revokeObjectURL(media.src);
        resolve(media.duration);
      };

      media.onerror = () => {
        reject(new Error("Failed to load media metadata."));
      };

      media.src = URL.createObjectURL(file);
    });
  };

  const uploadFileToServer = async (file) => {
    setUploading(true);
    try {
      const duration = await getMediaDuration(file);
      const metaData = {
        original_filename: file.name.replace(/\.[^/.]+$/, ""),
        format: file.type.split("/").pop(),
        created_at: new Date().toISOString(),
        duration: duration,
      };

      const formData = new FormData();
      formData.append("file", file);
      formData.append("data", JSON.stringify(metaData));

      const res = await uploadMedia(formData);

      if (res?.status === "success") {
        setUploads([res.upload, ...uploads]);
        toast("Transcript generated successfully!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload failed", error);
      toast("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  };

  const handleFiles = (files) => {
    const file = files[0];
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      alert("Only MP3, M4A, WAV, or MP4 files are allowed.");
      return;
    }

    uploadFileToServer(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    handleFiles(event.dataTransfer.files);
  };

  const handleFileSelect = (event) => {
    handleFiles(event.target.files);
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const PageChange = (val) => {
    setPageNumber(val);
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(url.search);
    searchParams.set("pageValue", val);
    url.search = searchParams.toString();
    window.history.pushState({ path: url.href }, "", url.href);
  };

  const manageSortBy = (a, b) => {
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(url.search);
    searchParams.set("sortBy", a);
    searchParams.set("sortOrder", b);
    url.search = searchParams.toString();
    window.history.pushState({ path: url.href }, "", url.href);

    setSortBy(a);
    setSortOrder(b);
  };

  useEffect(() => {
    const getuploadData = async () => {
      setLoading(true);
      const queryParams = new URLSearchParams({
        rowsPerPage: rowsPerPage,
        pageNumber: pageNumber,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      try {
        const res = await getuploadMedia(queryParams);
        if (res?.status === "success") {
          setUploads(res?.result?.transcriptions || []);
          setTotalRows(res?.totalRows || 0);
        } else {
          throw new Error("Failed to fetch uploads");
        }
      } catch (e) {
        toast(
          e?.response?.data?.message || "An error occurred. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    getuploadData();
  }, [rowsPerPage, pageNumber, sortBy, sortOrder]);

  return (
    <>
      <ToastContainer />
      <NavBar />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "200px 1fr",
          height: "100vh",
        }}
      >
        <SideBar />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            background: "#fff",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              background: "#fff",
              width: "100%",
              padding: "10px 40px",
            }}
          >
            <div className="main-container scroll-nice">
              <div className="header-container">
                <div className="header-sub-container">
                  <div className="upload-icon-container">
                    <img src={documentSvg} alt="" className="upload-icon" />
                  </div>
                  <h2 className="upload-header-hd">
                    Upload an audio or video file to generate a transcript
                  </h2>
                  <p className="upload-header-para">
                    Depending on the size of the audio file, it will be
                    processed &amp; transcribed in 5 - 10 mins
                  </p>
                </div>
              </div>

              <div
                className="dropzone-main"
                onClick={handleClick}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                style={{
                  position: "relative",
                  border: "2px dashed #aaa",
                  padding: "20px",
                  textAlign: "center",
                  borderRadius: "10px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="file"
                  accept=".mp3,.m4a,.wav,.mp4"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  style={{ display: "none" }}
                />
                <p className="smaller">
                  <img width="20" height="20" src={upload} alt="upload" />
                  {uploading ? (
                    "Uploading..."
                  ) : (
                    <>
                      Drag and drop <b className="highlighted">MP3, M4A, WAV</b>{" "}
                      or <b className="highlighted">MP4</b> file here, or click
                      to select.
                    </>
                  )}
                </p>
              </div>

              <br />
              <UploadTable
                totalRows={totalRows}
                pageNumber={pageNumber}
                setRowsPerPage={(data) => {
                  setRowsPerPage(data);
                  const url = new URL(window.location.href);
                  const searchParams = new URLSearchParams(url.search);
                  searchParams.set("rowsPerPage", data);
                  url.search = searchParams.toString();
                  window.history.pushState({ path: url.href }, "", url.href);
                }}
                setData={setUploads}
                rowsPerPage={rowsPerPage}
                setPageNumber={PageChange}
                data={uploads}
                pending={loading}
                setLoading={setLoading}
                manageSortBy={manageSortBy}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UploadScreen;
